import { Toast } from "../Notifications/Toast.js";

const REPORT_INFO_API = "/reports/get-user-ai-report-info";        // toasts (+ summary bar on worklist)
const WORKLIST_COUNT_API = "/worklist/get-unread-ai-report-counts"; // badge count (same as worklist)

export class AiReportMonitor {
    constructor() {
        this.init();
    }

    init() {
        document.body.addEventListener("toast-notification-click", this.handleToastClick.bind(this));
        // spread clients over the minute after the mark so every open tab doesn't hit the DB at :00/:05 (VETALL-34328)
        document.body.addEventListener("five-minute-mark", () => setTimeout(() => this.check(), Math.random() * 60000));
        // immediate check on load; unreadOnly=true so we don't toast reports added before load
        this.check(true);
    }

    isOnWorklist() {
        // WorkList.js owns the badge on the worklist page; AiReportMonitor stands down there.
        return !!document.querySelector("work-list");
    }

    check(unreadOnly = false) {
        const onWorklist = this.isOnWorklist();

        // --- toasts (+ summary bar on worklist) ---
        const reportUrl = onWorklist ? `${REPORT_INFO_API}?iurd=1` : REPORT_INFO_API;
        fetch(reportUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then(data => {
                if (!data.success) {
                    return;
                }
                if (!unreadOnly && (data.recently_added_reports || 0) > 0) {
                    this.showToast(data);
                }
                if (onWorklist) {
                    const summaryBar = document.querySelector("worklist-summary-bar");
                    if (summaryBar) {
                        summaryBar.refreshUnreadReports(data.unread_reports || 0, data.unread_tier_counts || {});
                    }
                }
            })
            .catch(error => {
                console.error("Error fetching report info:", error);
            });

        // --- sidebar badge (off-worklist only) ---
        if (!onWorklist) {
            this.refreshBadgeCount();
        }
    }

    refreshBadgeCount() {
        // mirror WorkList.js default range exactly (client-side, 60 days) so the numbers agree
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 60);
        const endDate = new Date();

        const formData = new FormData();
        formData.append("start_date", startDate.toISOString().slice(0, 10));
        formData.append("end_date", endDate.toISOString().slice(0, 10));
        // NO case_ids and NO other filters → unfiltered count; case_counts loop no-ops server-side

        fetch(WORKLIST_COUNT_API, {
            method: "POST",
            body: formData,
            expect: "json"
        })
            .then(response => {
                if (response.status === 200) {
                    return response.json();
                }
                return null;
            })
            .then(data => {
                if (data && data.success) {
                    this.updateBadge(data.total_unread_count);
                }
            })
            .catch(error => {
                console.error("Error fetching worklist count:", error);
            });
    }

    updateBadge(count) {
        let badge = document.querySelector("#unread-ai-report-case-count-badge");
        if (!badge) {
            badge = this.createBadge();
        }
        if (!badge) {
            return; // sidebar/menu not present (e.g. public pages)
        }
        const link = badge.querySelector("a");
        if (link) {
            link.textContent = count;
        }
        if (count > 0) {
            badge.style.display = "flex";
        } else {
            badge.style.display = "none";
        }
    }

    createBadge() {
        const menuSidebar = document.querySelector("menu-sidebar");
        if (!menuSidebar) {
            return null;
        }
        const worklistLink = menuSidebar.querySelector("a[href='/worklist/clinic']");
        if (!worklistLink) {
            return null;
        }

        const badge = document.createElement("div");
        badge.id = "unread-ai-report-case-count-badge";
        const popOver = document.createElement("pop-over");
        popOver.textContent = "Cases with unread AI reports";
        const link = document.createElement("a");
        link.href = "javascript:void(0)";
        badge.appendChild(popOver);
        badge.appendChild(link);
        badge.style.display = "none";
        worklistLink.appendChild(badge);

        // off-worklist click NAVIGATES to the worklist (unlike WorkList.js which filters in place)
        badge.addEventListener("click", () => {
            window.location.href = "/worklist/clinic";
        });
        return badge;
    }

    // --- toasts (ported verbatim from old BodyWrapper) ---
    showToast(data) {
        const recently_added_reports = data.recently_added_reports || 0;
        let message = "";
        if (recently_added_reports === 1) {
            message = this.getSingularReportMessage(data);
        } else {
            message = this.getMultipleReportsMessage(data);
        }

        const caseIds = new Set();
        data.recently_added_report_data.forEach(report => {
            caseIds.add(report.case_id);
        });
        const commaList = Array.from(caseIds).join(",");
        const datasetList = [
            { key: "case_ids", value: commaList }
        ];

        const toast = new Toast({
            bodyContent: message,
            headerContent: "Unread Report Alert",
            delay: 10000, // 10 seconds
            containerClassList: ["unread-report-toast-container"],
            datasetList: datasetList,
            hideOthers: true
        });
        toast.show();
    }

    getSingularReportMessage(data) {
        const report = data.recently_added_report_data[0];
        let message = "One new report ready - ";
        if (report.findings.length == 1) {
            message += `${ report.findings[0] }`;
        } else {
            message += `${ report.findings[0] } and ${ report.findings.length - 1 } others`;
        }
        message += `<br/> Click on this message to review the case.`;
        return message;
    }

    getMultipleReportsMessage(data) {
        const recently_added_reports = data.recently_added_reports || 0;
        // Tally reports by tier label, ordered by urgency
        const tierOrder = ["Critical", "High", "Moderate", "Low"];
        const tierCounts = {};
        data.recently_added_report_data.forEach(report => {
            tierCounts[report.label] = (tierCounts[report.label] || 0) + 1;
        });

        const tierParts = tierOrder
            .filter(label => tierCounts[label])
            .map(label => `${tierCounts[label]} ${label}`);

        let message = `${recently_added_reports} new reports ready`;
        if (tierParts.length) {
            message += ` - ${tierParts.join(", ")}`;
        }
        message += `<br/> Click on this message to review the cases.`;
        return message;
    }

    handleToastClick(event) {
        const toastElem = event.detail.originalEvent.currentTarget;
        const container = toastElem.closest(".unread-report-toast-container");
        if (toastElem && container) {
            window.location.href = "/worklist/clinic?CaseID=" + toastElem.dataset.case_ids;
        }
    }
}

// guard against the module loading after DOMContentLoaded has already fired
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => new AiReportMonitor());
} else {
    new AiReportMonitor();
}
